import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useAuth } from "@finitless/shared/contexts";
import { useUserStore } from "@finitless/shared/stores";
// Socket will be passed as a prop or imported by the app
import { appConfig } from "@finitless/shared/config";
import { useApiStore } from "@finitless/shared/stores";
import { addBreadcrumb, captureException } from "@finitless/shared/config";
import {
  CONNECTION_STATES,
  getConnectErrorTelemetryDecision,
  getDisconnectTelemetryDecision,
  getReconnectFailedTelemetryDecision,
  MAX_RECONNECT_ATTEMPTS,
  normalizeReconnectAttempt,
  resolveDisconnectState,
} from "./websocketTelemetry";

const WS_BREADCRUMB_MESSAGES = Object.freeze({
  DISCONNECTED: "WebSocket disconnected",
  RECONNECT_ATTEMPT: "WebSocket reconnect attempt",
  CONNECT_ERROR_DEDUPED: "WebSocket connect_error (deduped in cycle)",
  RECONNECTED: "WebSocket reconnected successfully",
  RECONNECT_ERROR: "WebSocket reconnection error",
  RECONNECT_FAILED: "WebSocket reconnection failed",
});

/**
 * WebsocketContext
 * Manages socket connection and provides easy interface for components
 */
export const WebsocketContext = createContext();

/**
 * WebsocketProvider
 * Provides socket connection management with authentication and project context
 */
export const WebsocketProvider = ({ children, Socket }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const currentProject = useApiStore((state) => state.project);
  const user = useUserStore((state) => state.user);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState(
    CONNECTION_STATES.OFFLINE,
  );
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [disconnectReason, setDisconnectReason] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const [roomsJoined, setRoomsJoined] = useState(false);
  const [conversationRoomsJoined, setConversationRoomsJoined] = useState(false);
  const hasCapturedConnectErrorInCycle = useRef(false);
  const reconnectAttemptRef = useRef(0);

  // Centralized event listeners registry
  const eventListeners = useRef(new Map());

  /**
   * Register an event listener
   */
  const registerEventListener = useCallback((event, callback, id) => {
    if (!eventListeners.current.has(event)) {
      eventListeners.current.set(event, new Map());
    }
    eventListeners.current.get(event).set(id, callback);
  }, []);

  /**
   * Unregister an event listener
   */
  const unregisterEventListener = useCallback((event, id) => {
    if (eventListeners.current.has(event)) {
      eventListeners.current.get(event).delete(id);
    }
  }, []);

  /**
   * Emit event to all registered listeners
   */
  const emitToListeners = useCallback((event, data) => {
    if (eventListeners.current.has(event)) {
      const listeners = eventListeners.current.get(event);
      listeners.forEach((callback) => {
        try {
          callback(data);
        } catch {
          /* noop */
        }
      });
    }
  }, []);

  // Initialize socket when authenticated
  useEffect(() => {
    if (authLoading || !Socket) return;

    if (isAuthenticated && user?.id) {
      const socketInstance = new Socket({
        url: appConfig.socket.url,
        project: currentProject,
        accessToken: localStorage.getItem("auth_token") || "",
      });

      setSocket(socketInstance);
      setConnectionError(null);
    } else {
      // Clean up socket when not authenticated
      if (socket) {
        socket.close();
        setSocket(null);
        setIsConnected(false);
        setConnectionState(CONNECTION_STATES.OFFLINE);
        setReconnectAttempt(0);
        reconnectAttemptRef.current = 0;
        setDisconnectReason(null);
        setRoomsJoined(false);
        setConversationRoomsJoined(false);
      }
    }
  }, [isAuthenticated, user?.id, authLoading]);

  // Connect socket when ready
  useEffect(() => {
    if (socket && isAuthenticated && user?.id) {
      socket.connect();
    }

    return () => {
      if (socket) {
        socket.close();
      }
    };
  }, [socket, isAuthenticated, user?.id]);

  // Helper function to join orders rooms based on user level
  const joinOrdersRooms = useCallback(() => {
    if (!socket || !user?.id || !isConnected || roomsJoined || !currentProject)
      return;

    // Determine role based on user level
    const role = user.level === 0 ? "manager" : "customer";

    // Join main orders room with user info
    socket.join({
      room: "orders",
      project: currentProject,
      user_id: user.id,
      role: role,
    });

    // Join specific rooms based on user level
    if (user.level === 0) {
      // Admin level - join global rooms
      socket.join("orders");
    } else {
      // Regular user level - join user-specific rooms
      socket.join(`orders_${user.id}`);
    }

    setRoomsJoined(true);
  }, [socket, user, isConnected, roomsJoined, currentProject]);

  // Helper function to join conversation rooms based on user level
  const joinConversationRooms = useCallback(() => {
    if (
      !socket ||
      !user?.id ||
      !isConnected ||
      conversationRoomsJoined ||
      !currentProject
    )
      return;

    // Determine role based on user level
    const role = user.level === 0 ? "manager" : "customer";

    // Join main conversations room with user info
    socket.join({
      room: "conversations",
      project: currentProject,
      user_id: user.id,
      role: role,
    });

    // Join specific rooms based on user level
    if (user.level === 0) {
      // Admin level - join global conversation rooms
      socket.join("conversations");
    } else {
      // Regular user level - join user-specific conversation rooms
      socket.join(`conversations_${user.id}`);
    }

    setConversationRoomsJoined(true);
  }, [socket, user, isConnected, conversationRoomsJoined, currentProject]);

  // Helper function to leave orders rooms
  const leaveOrdersRooms = useCallback(() => {
    if (!socket || !user?.id || !roomsJoined) return;

    if (user.level === 0) {
      socket.leave("orders");
    } else {
      socket.leave(`orders_${user.id}`);
    }

    setRoomsJoined(false);
  }, [socket, user, roomsJoined]);

  // Helper function to leave conversation rooms
  const leaveConversationRooms = useCallback(() => {
    if (!socket || !user?.id || !conversationRoomsJoined) return;

    if (user.level === 0) {
      socket.leave("conversations");
    } else {
      socket.leave(`conversations_${user.id}`);
    }

    setConversationRoomsJoined(false);
  }, [socket, user, conversationRoomsJoined]);

  // Centralized room management - join rooms when connected
  useEffect(() => {
    if (socket && isConnected && user?.id) {
      joinOrdersRooms();
      // Only join general conversation rooms, not specific conversation rooms
      // Specific conversation rooms are handled by useConversationRoom hook
      joinConversationRooms();
    }
  }, [socket, isConnected, user?.id, joinOrdersRooms, joinConversationRooms]);

  // Cleanup rooms when component unmounts or user changes
  useEffect(() => {
    return () => {
      if (socket && user?.id) {
        leaveOrdersRooms();
        leaveConversationRooms();
      }
    };
  }, [socket, user?.id, leaveOrdersRooms, leaveConversationRooms]);

  // Handle connection events
  useEffect(() => {
    if (!socket?.socket) return;
    const socketManager = socket.socket.io;

    const handleConnect = () => {
      setIsConnected(true);
      setConnectionState(CONNECTION_STATES.CONNECTED);
      setReconnectAttempt(0);
      reconnectAttemptRef.current = 0;
      setDisconnectReason(null);
      setConnectionError(null);
      hasCapturedConnectErrorInCycle.current = false;
      localStorage.setItem(
        "websocket-connected-date",
        new Date().toISOString(),
      );
    };

    const handleDisconnect = (reason, details) => {
      setIsConnected(false);
      setRoomsJoined(false);
      setConversationRoomsJoined(false);
      hasCapturedConnectErrorInCycle.current = false;

      const disconnectDecision = resolveDisconnectState({
        reason,
        isManagerActive: Boolean(socket.socket?.active),
      });
      setConnectionState(disconnectDecision.nextConnectionState);
      setDisconnectReason(reason || "unknown");

      const disconnectTelemetryDecision = getDisconnectTelemetryDecision();
      if (disconnectTelemetryDecision.addBreadcrumb) {
        addBreadcrumb({
          category: "websocket.connection",
          level: "info",
          message: WS_BREADCRUMB_MESSAGES.DISCONNECTED,
          data: {
            socket_id: socket.getId(),
            reason: reason || "unknown",
            details: details || null,
            connection_status: disconnectDecision.nextConnectionState,
            timestamp: new Date().toISOString(),
            user_id: user?.id,
            project: currentProject,
            pathname:
              typeof window !== "undefined"
                ? window.location.pathname
                : undefined,
            online:
              typeof navigator !== "undefined" ? navigator.onLine : undefined,
          },
        });
      }

      if (disconnectDecision.shouldAttemptManualReconnect) {
        try {
          socket.socket?.connect?.();
          setConnectionState(CONNECTION_STATES.RECONNECTING);
        } catch {
          setConnectionState(CONNECTION_STATES.OFFLINE);
        }
      }
    };

    const handleReconnectAttempt = (attempt) => {
      const normalizedAttempt = normalizeReconnectAttempt(attempt);
      setConnectionState(CONNECTION_STATES.RECONNECTING);
      setReconnectAttempt(normalizedAttempt);
      reconnectAttemptRef.current = normalizedAttempt;
      addBreadcrumb({
        category: "websocket.reconnect",
        level: "info",
        message: WS_BREADCRUMB_MESSAGES.RECONNECT_ATTEMPT,
        data: {
          attempt: normalizedAttempt,
          max_attempts: MAX_RECONNECT_ATTEMPTS,
          socket_id: socket.getId(),
          user_id: user?.id,
          project: currentProject,
          timestamp: new Date().toISOString(),
        },
      });
    };

    const handleConnectError = (error) => {
      setConnectionError(error);
      setConnectionState(CONNECTION_STATES.RECONNECTING);

      const connectErrorTelemetryDecision = getConnectErrorTelemetryDecision({
        hasCapturedInCycle: hasCapturedConnectErrorInCycle.current,
      });

      if (connectErrorTelemetryDecision.captureException) {
        hasCapturedConnectErrorInCycle.current = true;
        captureException(error, {
          socket: {
            connection_status: "error",
            timestamp: new Date().toISOString(),
            user_id: user?.id,
            project: currentProject,
            error_type: "connection_error",
          },
        });
      }

      if (connectErrorTelemetryDecision.addBreadcrumb) {
        addBreadcrumb({
          category: "websocket.connection",
          level: "warning",
          message: WS_BREADCRUMB_MESSAGES.CONNECT_ERROR_DEDUPED,
          data: {
            error_message: error?.message || "Unknown connection error",
            user_id: user?.id,
            project: currentProject,
            timestamp: new Date().toISOString(),
          },
        });
      }
    };

    const handleReconnect = (attempt) => {
      setConnectionState(CONNECTION_STATES.CONNECTED);
      setReconnectAttempt(0);
      reconnectAttemptRef.current = 0;
      setDisconnectReason(null);
      hasCapturedConnectErrorInCycle.current = false;
      addBreadcrumb({
        category: "websocket.reconnect",
        level: "info",
        message: WS_BREADCRUMB_MESSAGES.RECONNECTED,
        data: {
          socket_id: socket.getId(),
          reconnect_attempt: normalizeReconnectAttempt(attempt),
          connection_status: CONNECTION_STATES.CONNECTED,
          timestamp: new Date().toISOString(),
          user_id: user?.id,
          project: currentProject,
        },
      });
    };

    const handleReconnectError = (error) => {
      setConnectionState(CONNECTION_STATES.RECONNECTING);
      addBreadcrumb({
        category: "websocket.reconnect",
        level: "warning",
        message: WS_BREADCRUMB_MESSAGES.RECONNECT_ERROR,
        data: {
          connection_status: CONNECTION_STATES.RECONNECTING,
          timestamp: new Date().toISOString(),
          user_id: user?.id,
          project: currentProject,
          error_type: "reconnect_error",
          error_message: error?.message || "Unknown reconnection error",
        },
      });
    };

    const handleReconnectFailed = () => {
      const error = new Error("Failed to reconnect to websocket");
      setConnectionError(error);
      setConnectionState(CONNECTION_STATES.OFFLINE);
      setDisconnectReason("reconnect_failed");

      const reconnectFailedTelemetryDecision =
        getReconnectFailedTelemetryDecision();

      if (reconnectFailedTelemetryDecision.addBreadcrumb) {
        addBreadcrumb({
          category: "websocket.reconnect",
          level: "error",
          message: WS_BREADCRUMB_MESSAGES.RECONNECT_FAILED,
          data: {
            connection_status: CONNECTION_STATES.OFFLINE,
            timestamp: new Date().toISOString(),
            user_id: user?.id,
            project: currentProject,
            reconnect_attempt: reconnectAttemptRef.current,
            max_attempts: MAX_RECONNECT_ATTEMPTS,
          },
        });
      }

      if (reconnectFailedTelemetryDecision.captureException) {
        captureException(error, {
          socket: {
            connection_status: "reconnect_failed",
            timestamp: new Date().toISOString(),
            user_id: user?.id,
            project: currentProject,
            error_type: "reconnection_failed",
            reconnect_attempt: reconnectAttemptRef.current,
            max_attempts: MAX_RECONNECT_ATTEMPTS,
          },
        });
      }
    };

    // Add event listeners
    socket.socket.on("connect", handleConnect);
    socket.socket.on("disconnect", handleDisconnect);
    socket.socket.on("connect_error", handleConnectError);
    socketManager?.on("reconnect_attempt", handleReconnectAttempt);
    socketManager?.on("reconnect", handleReconnect);
    socketManager?.on("reconnect_error", handleReconnectError);
    socketManager?.on("reconnect_failed", handleReconnectFailed);

    // Centralized order event listeners
    const handleOrdersRegister = (data) => {
      emitToListeners("orders_register", data);
    };

    const handleUpdateOrder = (data) => {
      emitToListeners("update_order", data);
    };

    // Centralized conversation event listeners
    const handleConversationCreated = (data) => {
      emitToListeners("conversation.created", data);
    };

    const handleConversationMessageCreated = (data) => {
      emitToListeners("conversation.message.created", data);
    };

    const handleConversationUpdated = (data) => {
      emitToListeners("conversation.updated", data);
    };

    socket.socket.on("orders_register", handleOrdersRegister);
    socket.socket.on("update_order", handleUpdateOrder);
    socket.socket.on("conversation.created", handleConversationCreated);
    socket.socket.on(
      "conversation.message.created",
      handleConversationMessageCreated,
    );
    socket.socket.on("conversation.updated", handleConversationUpdated);

    // Cleanup
    return () => {
      if (socket.socket) {
        socket.socket.off("connect", handleConnect);
        socket.socket.off("disconnect", handleDisconnect);
        socket.socket.off("connect_error", handleConnectError);
        socketManager?.off("reconnect_attempt", handleReconnectAttempt);
        socketManager?.off("reconnect", handleReconnect);
        socketManager?.off("reconnect_error", handleReconnectError);
        socketManager?.off("reconnect_failed", handleReconnectFailed);

        // Cleanup centralized order event listeners
        socket.socket.off("orders_register", handleOrdersRegister);
        socket.socket.off("update_order", handleUpdateOrder);

        // Cleanup centralized conversation event listeners
        socket.socket.off("conversation.created", handleConversationCreated);
        socket.socket.off(
          "conversation.message.created",
          handleConversationMessageCreated,
        );
        socket.socket.off("conversation.updated", handleConversationUpdated);
      }
    };
  }, [socket?.socket]);

  const value = {
    socket,
    isConnected,
    connectionState,
    reconnectAttempt,
    maxReconnectAttempts: MAX_RECONNECT_ATTEMPTS,
    disconnectReason,
    connectionError,
    user,
    // Helper methods
    joinRoom: (room) => socket?.join(room),
    leaveRoom: (room) => socket?.leave(room),
    joinOrdersRooms,
    leaveOrdersRooms,
    joinConversationRooms,
    leaveConversationRooms,
    on: (event, callback) => socket?.on(event, callback),
    off: (event, callback) => socket?.off(event, callback),
    emit: (event, data) => socket?.emit(event, data),
    // Centralized event system
    registerEventListener,
    unregisterEventListener,
    emitToListeners,
  };

  return (
    <WebsocketContext.Provider value={value}>
      {children}
    </WebsocketContext.Provider>
  );
};

/**
 * Hook to use websocket context
 */
export const useWebsocket = () => {
  const context = useContext(WebsocketContext);
  if (!context) {
    throw new Error("useWebsocket must be used within a WebsocketProvider");
  }
  return context;
};

export default WebsocketContext;
